import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AppShell, Box, Flex, useMantineTheme } from '@mantine/core';
import AppFooter from '@/components/AppFooter';
import ErrorBoundary from '@/components/ErrorBoundary';
import SkipLink from '@/components/SkipLink';
import AppHeader from '@/components/app/AppHeader';
import { ActiveNavProvider } from '@/components/app/activeNav';
import ImpersonationBanner from '@/components/app/ImpersonationBanner';
import RequiredDataBanner from '@/components/app/RequiredDataBanner';
import ChatProvider from '@/chat/ChatProvider';
import { unlockAudioOnFirstGesture } from '@/lib/notificationSound';
import { useRealtime } from '@/realtime/useRealtime';
import { useOnboarding } from '@/hooks/useOnboarding';
import { ROUTES } from '@/routes/paths';
import { headerClass } from '@/components/header/HeaderShell';

// Оболочка кабинета: закреплённая шапка (общая с публичной частью), баннер обязательных данных,
// контент страницы. Подвал — везде, где есть верхнее меню (sticky-footer, как в PublicLayout).
export default function AppLayout() {
  const { headerHeight } = useMantineTheme().other;
  // Real-time: SSE-поток уведомлений (системные → инвалидация кэша, видимые → колокольчик).
  // Живёт пока пользователь в кабинете; на logout AppLayout размонтируется и поток останавливается.
  useRealtime();

  // Политика автовоспроизведения: звук уведомлений можно проигрывать только после пользовательского
  // жеста — разблокируем AudioContext на первом клике/нажатии клавиши в кабинете (одноразово).
  useEffect(() => unlockAudioOnFirstGesture(), []);

  // Во время онбординга на «Главной» жёлтую полосу обязательных данных скрываем — её замещает
  // богатая карточка «Первые шаги» (без дублирования). На остальных страницах полоса остаётся.
  const { pathname } = useLocation();
  const onboarding = useOnboarding();
  const hideRequiredDataBanner = pathname === ROUTES.APP && onboarding.active;

  return (
    // ChatProvider оборачивает хедер и контент: бейдж непрочитанного (P3) и страница чата делят
    // один matrix-js-sdk клиент. SDK грузится динамически внутри провайдера (отдельный чанк).
    <ChatProvider>
      {/* Провайдер охватывает и хедер, и контент: страница вне путей разделов (карточка заявки
          перевозчика) называет свой пункт меню, а подсвечивает его хедер. */}
      <ActiveNavProvider>
      <SkipLink />
      <AppShell header={{ height: headerHeight }} padding={0}>
        <AppShell.Header withBorder={false} className={headerClass}>
          <AppHeader />
        </AppShell.Header>

        <AppShell.Main id="main" tabIndex={-1} className="app-scope">
          <Flex direction="column" mih={`calc(100dvh - ${headerHeight}px)`}>
            <ImpersonationBanner />
            {/* Фон кабинета белый: язык «Бюллетеня» разделяет блоки линиями и пространством, а не карточками на сером. */}
            <Box style={{ flexGrow: 1 }}>
              {!hideRequiredDataBanner && <RequiredDataBanner />}
              {/* Ошибка страницы не должна убирать шапку и меню: человек уходит в другой раздел, ключ снимает ошибку. */}
              <ErrorBoundary key={pathname} place="cabinet">
                <Outlet />
              </ErrorBoundary>
            </Box>
            <AppFooter />
          </Flex>
        </AppShell.Main>
      </AppShell>
      </ActiveNavProvider>
    </ChatProvider>
  );
}
